import { useEffect, useMemo, useRef } from 'react';
import { createContext } from '@shared/utils/createContext';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { DraftData, ThemeJson } from './CardCustomizer.types';
import { useUpdateStationeryDraftMutation } from './CardCustomizer.graphql.generated';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { throttle } from 'lodash-es';

const [DraftSaverCancelProvider, useDraftSaverCancel] = createContext<() => void>({
  name: 'DraftSaverCancel',
});

export { DraftSaverCancelProvider, useDraftSaverCancel };

export const useDraftSaver = ({ draft, draftId, disable }: { draft: DraftData; draftId: string; disable?: boolean }) => {
  const data = useEventInfo();
  // Initializing ref value to null instead of the `draft.config`
  // so that we can force an update on the first mount.
  //
  // This is particularly important when going from public shop to admin
  // and we need to associate the draft to an event.
  const draftRef = useRef<ThemeJson | null>(null);
  const eventId = data.eventInfo?.eventId;
  const [update, { loading }] = useUpdateStationeryDraftMutation();

  const updateDraft = useEventCallback(() => {
    if (eventId) {
      update({
        variables: {
          payload: {
            id: draftId,
            eventId,
            cardJSON: draft.config,
          },
        },
      });
    }
  });

  // Memoize the throttled call
  const throttledUpdate = useMemo(() => {
    // Throttle the call to exectue once every 2 seconds
    return throttle(updateDraft, 2000);
  }, [updateDraft]);

  useEffect(() => {
    if (disable) {
      return;
    }

    // If draft prop is different than the locally stored draft, update the draft
    if (draftRef.current !== draft.config) {
      // Don't execute if an update is already underway
      if (loading) {
        return;
      }

      // Update the local draft ref
      draftRef.current = draft.config;
      throttledUpdate();
    }
  }, [draft.config, throttledUpdate, loading, disable]);

  return { cancelPendingSave: () => throttledUpdate.cancel() };
};
